import { dpString, parseNumberString } from "common/numbers";

// The triage rule vocabulary, in one place: which fields rules can name, which of them
// are layer-scoped, the operators, what carries into a submission, and the shared
// wording helpers. Everything else imports from here — nothing redefines these.
export const FIELD_OPTIONS = [
  { value: "insured", label: "Insured Name" },
  { value: "insuredAddress", label: "Insured Address" },
  { value: "broker", label: "Broker" },
  { value: "premium", label: "Premium" },
  { value: "total_insured_value", label: "Total Insured Value" },
  { value: "inception", label: "Inception Date" },
  { value: "expiration", label: "Expiration Date" },
  { value: "line_of_business", label: "Line of Business" },
  { value: "state", label: "State" },
  { value: "naics", label: "NAICS Code" },
  { value: "industryClass", label: "Industry Class" },
  { value: "transaction", label: "Transaction Type" },
  // Layer-scoped fields evaluate in code against every pricing layer the ingestion
  // agent extracted from the documents (one verdict per layer) — never via the LLM,
  // and never against the web. Must stay in sync with LAYER_FIELD_TO_TOWER_KEY in
  // services/ai_agents/submission_triage/layer_scoring.py.
  { value: "layerLimit", label: "Layer: Limit", layer: true },
  { value: "layerAttachment", label: "Layer: Attachment", layer: true },
  { value: "layerGrossPremium", label: "Layer: Gross Premium", layer: true },
  { value: "layerShareOfLine", label: "Layer: Share of Line", layer: true },
  { value: "layerCoverage", label: "Layer: Coverage Line", layer: true },
];

export const LAYER_FIELDS = new Set(
  FIELD_OPTIONS.filter((f) => f.layer).map((f) => f.value)
);

export const isLayerField = (fieldName) => LAYER_FIELDS.has(fieldName);

// Ingestion fields and program state share one vocabulary, so carrying a field forward
// is a straight copy — this list only says which fields triage may carry.
export const CARRY_FORWARD_FIELDS = [
  "insured",
  "insuredAddress",
  "inception",
  "expiration",
  "industryClass",
  "naics",
  "brokerage",
  "currency",
  "maximumPossibleLoss",
  "reference",
  "riskDescription",
];

// Must stay a subset of RuleOperator in services/ai_agents/submission_triage/definitions.py
// — an operator the enum does not define makes the whole ruleset fail validation.
export const OPERATORS = [
  { value: "exists", label: "exists" },
  { value: "equals", label: "equals" },
  { value: "contains", label: "contains" },
  { value: "greater_than", label: "greater than" },
  { value: "less_than", label: "less than" },
  { value: "in_set", label: "is in set" },
];

export const isGroup = (rule) => rule?.kind === "group";

// "(premium AND broker) AND (state OR naics)" — shown on the group header so the reader
// can see the whole expression without expanding every level.
export const describeEntry = (entry) => {
  if (!isGroup(entry)) return entry?.field_name || "…";
  const joiner = ` ${(entry.combinator || "and")
    .toUpperCase()
    .replace("_", " ")} `;
  const inner = (entry.rules || []).map(describeEntry).join(joiner);
  return `(${inner})`;
};

// Whether a submission is in scope, or null when its ruleset sets no threshold.
//
// The threshold can be edited long after a report was produced, so the verdict is derived
// here from the stored score and the CURRENT threshold rather than read out of the report.
// That is what makes an edit reclassify the pipeline with no re-run. Mirrors is_in_scope in
// services/ai_agents/submission_triage/definitions.py, which decides it at triage time.
export const isInScope = (weightedScore, threshold) => {
  if (threshold == null || typeof weightedScore !== "number") return null;
  return weightedScore >= threshold;
};

// An operator as a reader sees it here: "greater than", "is in set". Exported for the
// report's expression rows, so the two never phrase one operator two ways.
export const operatorLabel = (operator) =>
  OPERATORS.find((o) => o.value === operator)?.label || operator || "";

// A field's name as a reader sees it here: "Insured Name", "Layer: Attachment". Exported
// because the report showed rule rows by their raw program key — "insured", and worse
// "layerAttachment" — while this editor named the same field properly two clicks away.
// A combined rule's own name and a hand-typed field are already human, so they pass through.
export const fieldDisplayName = (fieldName) => {
  if (!fieldName) return "";
  const option = FIELD_OPTIONS.find((f) => f.value === fieldName);
  if (option) return option.label;
  if (/\s/.test(fieldName)) return fieldName;
  return fieldName
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
};

// "Premium greater than 1,000,000" — what a rule requires, in words, for readers outside
// this editor. Exported because the report drawer shows it on hover over a scored rule, and
// a second phrasing of the same rule in a second file is how the two drift apart.
export const describeRuleRequirement = (entry) => {
  if (!entry) return null;
  if (isGroup(entry)) {
    return `${entry.name || "Combined rule"}: ${describeEntry(entry)}`;
  }
  const field =
    FIELD_OPTIONS.find((f) => f.value === entry.field_name)?.label ||
    entry.field_name;
  const operator =
    OPERATORS.find((o) => o.value === entry.operator)?.label || entry.operator;
  // Thresholds are read as money more often than as digits, so 1000000 gets separators.
  // A set like "Aon, Marsh" or any non-numeric value is left exactly as typed.
  const raw = entry.value;
  const numeric = Array.isArray(raw) ? null : parseNumberString(raw ?? "");
  const value =
    raw === undefined || raw === null || raw === ""
      ? null
      : numeric == null
      ? String(raw)
      : dpString(2, { minDp: 0 })(numeric);
  const requirement = [field, operator, value].filter(Boolean).join(" ");
  return entry.required === false
    ? `${requirement} (scores only)`
    : requirement;
};

// Every top-level entry's requirement, keyed the way a report names it: a plain rule by its
// field, a combined rule by the label its synthesised row carries.
export const ruleRequirementsByName = (ruleset) => {
  const out = {};
  (ruleset?.rules || []).forEach((entry, index) => {
    const key = isGroup(entry)
      ? entry.name || `Combined rule ${index + 1}`
      : entry.field_name;
    if (key) out[key] = describeRuleRequirement(entry);
  });
  return out;
};
